export const parseOptionalNumber = (value: unknown): number | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'bigint') return Number(value);
  if (typeof value === 'object' && value && 'toString' in value) {
    return parseOptionalNumber(String((value as { toString: () => string }).toString()));
  }
  if (typeof value !== 'string') return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  const clean = trimmed.replace(/[^0-9,.-]/g, '');
  if (!clean) return null;

  const lastComma = clean.lastIndexOf(',');
  const lastDot = clean.lastIndexOf('.');
  let normalized = clean;

  if (lastComma !== -1 && lastDot !== -1) {
    normalized = lastComma > lastDot ? clean.replace(/\./g, '').replace(',', '.') : clean.replace(/,/g, '');
  } else if (lastComma !== -1) {
    normalized = clean.replace(/\./g, '').replace(',', '.');
  } else if ((clean.match(/\./g) ?? []).length > 1) {
    normalized = clean.replace(/\./g, '');
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

/** Bigint/number counts from raw SQL rows; null/undefined count as zero. */
export const toCount = (value: bigint | number | null | undefined): number =>
  value === null || value === undefined ? 0 : Number(value);

export const toIsoDay = (value: Date | string | null | undefined): string | null => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString().slice(0, 10);
};

export const toCnpjCandidates = (cnpjs: string[]) =>
  Array.from(
    new Set(
      cnpjs.flatMap((cnpj) => {
        const raw = (cnpj ?? '').trim();
        if (!raw) return [];
        const onlyDigits = raw.replace(/\D/g, '');
        return onlyDigits && onlyDigits !== raw ? [raw, onlyDigits] : [raw];
      }),
    ),
  );

export const formatDatePtBr = (value: string | null) => {
  if (!value) return '-';
  const parsed = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' }).format(parsed);
};

export const formatCurrencyPtBr = (value: number | null) => {
  if (value === null) return '-';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 2,
  }).format(value);
};

export const formatCompactCurrencyPtBr = (value: number | null) => {
  if (value === null) return '-';
  const abs = Math.abs(value);
  const numberFormatter = new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  if (abs >= 1_000_000_000) return `R$ ${numberFormatter.format(value / 1_000_000_000)} bi`;
  if (abs >= 1_000_000) return `R$ ${numberFormatter.format(value / 1_000_000)} mi`;
  if (abs >= 1_000) return `R$ ${numberFormatter.format(value / 1_000)} mil`;
  return formatCurrencyPtBr(value);
};

export const formatPercentPtBr = (value: number | null) => {
  if (value === null) return '-';
  return `${new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)}%`;
};
